import React, { useEffect, useState } from 'react';
import { FileText, Plus, Pencil, Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import Toast, { useToast } from '../common/Toast';
import { getCourseQuizzes, addCourseQuiz, updateCourseQuiz, deleteCourseQuiz } from '../../firebase/trainingAPI';
import QuizForm from './QuizForm';

const QuizManagement = ({ courses = [] }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizSaving, setQuizSaving] = useState(false);

  const { toasts, success, error, removeToast } = useToast();

  const loadQuizzes = async (courseId) => {
    try {
      setQuizzesLoading(true);
      const data = await getCourseQuizzes(courseId || '');
      setQuizzes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed loading quizzes:', err);
      error('Failed to load quizzes: ' + err.message);
    } finally {
      setQuizzesLoading(false);
    }
  };

  useEffect(() => {
    loadQuizzes(selectedCourseId);
  }, [selectedCourseId]);

  const handleSave = async (payload) => {
    try {
      setQuizSaving(true);
      if (editingQuiz?.id) {
        await updateCourseQuiz(editingQuiz.id, payload);
        success('Question updated successfully!');
      } else {
        await addCourseQuiz(payload);
        success('Question added successfully!');
      }
      setIsModalOpen(false);
      setEditingQuiz(null);
      await loadQuizzes(selectedCourseId);
    } catch (err) {
      console.error('Failed saving quiz:', err);
      error('Save failed: ' + err.message);
    } finally {
      setQuizSaving(false);
    }
  };

  const handleDelete = async (quiz) => {
    if (!window.confirm(`Delete question #${quiz.questionNumber}? This cannot be undone.`)) return;
    try {
      await deleteCourseQuiz(quiz.id);
      await loadQuizzes(selectedCourseId);
      success('Question deleted successfully!');
    } catch (err) {
      console.error('Failed deleting quiz:', err);
      error('Delete failed: ' + err.message);
    }
  };

  const filteredQuizzes = quizzes.filter((q) => !typeFilter || q.type === typeFilter);

  return (
    <div className="px-6 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Course Quizzes</h2>
          <p className="text-sm text-gray-500">{quizzes.length} question{quizzes.length !== 1 ? 's' : ''} total</p>
        </div>
        <button
          onClick={() => { setEditingQuiz({ courseId: selectedCourseId }); setIsModalOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus size={16} />
          Add Question
        </button>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-72">
          <label className="block text-xs font-medium text-gray-500 mb-1">Select Course</label>
          <select
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">-- All courses --</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div className="w-44">
          <label className="block text-xs font-medium text-gray-500 mb-1">Question Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">All Types</option>
            <option value="MCQ">MCQ</option>
            <option value="true/false">True/False</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {quizzesLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filteredQuizzes.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <FileText className="mx-auto mb-3 text-gray-300" size={40} />
            <p className="text-sm">{typeFilter ? 'No questions match the selected type.' : selectedCourseId ? 'No questions created for this course yet.' : 'No questions found.'}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Course</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Question</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Correct Answer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Marks</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(typeFilter ? filteredQuizzes : filteredQuizzes).map((quiz, displayIndex) => {
                const course = courses.find((c) => c.id === quiz.courseId);
                return (
                <tr key={quiz.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500 font-medium">{displayIndex + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[140px] truncate">{course?.title || quiz.courseId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 max-w-md truncate">{quiz.question}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      quiz.type === 'MCQ' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {quiz.type === 'MCQ' ? 'MCQ' : 'T/F'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-medium">
                    {quiz.type === 'MCQ'
                      ? ({ A: quiz.optionA, B: quiz.optionB, C: quiz.optionC, D: quiz.optionD })[quiz.correctAns] || quiz.correctAns
                      : quiz.correctAns}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{quiz.marks ?? 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingQuiz(quiz); setIsModalOpen(true); }}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(quiz)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => { if (!quizSaving) { setIsModalOpen(false); setEditingQuiz(null); } }}
        title={editingQuiz?.id ? `Edit Question #${editingQuiz.questionNumber}` : 'Add Question'}
        size="md"
      >
        <QuizForm
          courses={courses}
          initialData={editingQuiz}
          onSave={handleSave}
          onCancel={() => { setIsModalOpen(false); setEditingQuiz(null); }}
          saving={quizSaving}
        />
      </Modal>

      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            message={t.message}
            type={t.type}
            duration={t.duration}
            onClose={() => removeToast(t.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default QuizManagement;
